import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaPlus, FaEdit, FaTrash, FaMapMarkerAlt, FaStar, FaStar as FaStarSolid } from 'react-icons/fa';
import './Addresses.css';

const Addresses = () => {
  const { currentUser } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    landmark: '',
    default: false
  });
  
  const [isMobile, setIsMobile] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load user addresses
  useEffect(() => {
    if (currentUser) {
      loadAddresses();
    } else {
      setAddresses([]);
      setLoading(false);
    }
  }, [currentUser]);
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  const loadAddresses = async () => {
    try {
      setLoading(true);
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check for different possible field names - based on your DB structure it should be saved_addresses
        let addresses = userData.saved_addresses || [];
        
        // Ensure all addresses have an id property
        addresses = addresses.map((address, index) => ({
          ...address,
          id: address.id || `temp_${Date.now()}_${index}`
        }));
        
        setAddresses(addresses);
        

      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pinCode: '',
      landmark: '',
      default: false
    });
    setEditingAddress(null);
    setShowAddForm(false);
  };

  const goBackToMainView = () => {
    setShowAddForm(false);
    setEditingAddress(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle phone number input - automatically add +91 prefix
    if (name === 'phone') {
      let phoneValue = value;
      
      // Remove any existing +91 prefix first
      if (phoneValue.startsWith('+91')) {
        phoneValue = phoneValue.substring(3);
      }
      
      // If user enters digits, automatically add +91 prefix
      if (phoneValue.length > 0 && /^\d+$/.test(phoneValue)) {
        phoneValue = '+91' + phoneValue;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: phoneValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const validateForm = () => {
    // Clear previous validation states
    clearValidationErrors();
    
    let isValid = true;
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'pinCode'];
    
    for (const field of required) {
      if (!formData[field].trim()) {
        showFieldError(field, `Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        isValid = false;
      }
    }
    
    if (!/^\d{6}$/.test(formData.pinCode)) {
      showFieldError('pinCode', 'Please enter a valid 6-digit PIN code');
      isValid = false;
    }
    
    // Phone validation - handle +91 prefix
    let phoneToValidate = formData.phone;
    if (phoneToValidate.startsWith('+91')) {
      phoneToValidate = phoneToValidate.substring(3);
    }
    
    if (!/^\d{10}$/.test(phoneToValidate)) {
      showFieldError('phone', 'Please enter a valid 10-digit phone number');
      isValid = false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showFieldError('email', 'Please enter a valid email address');
      isValid = false;
    }
    
    if (!isValid) {
      showValidationSummary();
    }
    
    return isValid;
  };

  const showFieldError = (fieldName, message) => {
    const field = document.getElementById(fieldName);
    if (field) {
      field.classList.add('error');
      field.setAttribute('aria-invalid', 'true');
      
      // Add error message below the field
      let errorDiv = field.parentNode.querySelector('.field-error');
      if (!errorDiv) {
        errorDiv = document.createElement('div');
        errorDiv.className = 'field-error';
        field.parentNode.appendChild(errorDiv);
      }
      errorDiv.textContent = message;
    }
  };

  const clearValidationErrors = () => {
    // Remove error classes from all fields
    const fields = document.querySelectorAll('.address-form-compact input, .address-form-compact textarea');
    fields.forEach(field => {
      field.classList.remove('error');
      field.setAttribute('aria-invalid', 'false');
    });
    
    // Remove error messages
    const errorDivs = document.querySelectorAll('.field-error');
    errorDivs.forEach(div => div.remove());
    
    // Remove validation summary
    const summary = document.querySelector('.validation-summary');
    if (summary) summary.remove();
  };

  const showValidationSummary = () => {
    // Remove existing summary
    const existing = document.querySelector('.validation-summary');
    if (existing) existing.remove();
    
    // Create new summary
    const summary = document.createElement('div');
    summary.className = 'validation-summary';
    summary.innerHTML = `
      <div class="validation-icon">⚠️</div>
      <div class="validation-content">
        <h4>Please fix the following errors:</h4>
        <ul>
          <li>Fill in all required fields</li>
          <li>Enter a valid 10-digit phone number</li>
          <li>Enter a valid 6-digit PIN code</li>
        </ul>
      </div>
    `;
    
    // Insert before the form actions
    const formActions = document.querySelector('.form-actions-compact');
    if (formActions) {
      formActions.parentNode.insertBefore(summary, formActions);
    }
  };

  const showFormError = (message) => {
    showFormMessage(message, 'error');
  };

  const showFormSuccess = (message) => {
    showFormMessage(message, 'success');
  };

  const showMainPageSuccess = (message) => {
    setSuccessMessage(message);
    // Auto-clear after 6 seconds
    setTimeout(() => setSuccessMessage(''), 6000);
  };

  const showMainPageError = (message) => {
    setErrorMessage(message);
    // Auto-clear after 6 seconds
    setTimeout(() => setErrorMessage(''), 6000);
  };

  const showFormMessage = (message, type) => {
    // Remove existing messages
    const existing = document.querySelector('.form-message');
    if (existing) existing.remove();
    
    // Create new message
    const messageDiv = document.createElement('div');
    messageDiv.className = `form-message ${type}`;
    messageDiv.innerHTML = `
      <div class="message-icon">${type === 'error' ? '❌' : '✅'}</div>
      <div class="message-content">${message}</div>
    `;
    
    // Insert at the top of the form
    const form = document.querySelector('.address-form-compact');
    if (form) {
      form.insertBefore(messageDiv, form.firstChild);
      
      // Auto-remove after 6 seconds for better readability
      setTimeout(() => {
        if (messageDiv.parentNode) {
          messageDiv.remove();
        }
      }, 6000);
    }
  };

  const addAddress = async () => {
    if (!validateForm()) return;

    try {
      const newAddress = {
        ...formData,
        id: Date.now().toString(),
        savedAt: new Date().toISOString(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(), // Phone already has +91 prefix
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        pinCode: formData.pinCode.trim(),
        landmark: formData.landmark.trim()
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Get current user data to ensure we have the latest state
      const userDoc = await getDoc(userRef);
      let existingAddresses = [];
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        existingAddresses = userData.saved_addresses || [];
      }
      
      // If setting as default, remove default from other addresses
      let updatedAddresses = [...existingAddresses];
      if (newAddress.default) {
        updatedAddresses = updatedAddresses.map(addr => ({
          ...addr,
          default: false
        }));
      }
      
      // Ensure new address has proper ID and timestamp
      newAddress.id = newAddress.id || Date.now().toString();
      newAddress.savedAt = newAddress.savedAt || new Date().toISOString();
      
      updatedAddresses.push(newAddress);
      
      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userRef, {
        saved_addresses: updatedAddresses,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setAddresses(updatedAddresses);
      
      // Show premium success message on main page
      showMainPageSuccess(`Address for ${newAddress.firstName} ${newAddress.lastName} has been added to your collection`);
      
      // Add success animation
      const newCard = document.querySelector(`[data-address-id="${newAddress.id}"]`);
      if (newCard) {
        newCard.classList.add('success');
        setTimeout(() => newCard.classList.remove('success'), 600);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error adding address:', error);
      showFormError('Failed to add address. Please try again.');
    }
  };

  const updateAddress = async () => {
    if (!validateForm()) return;

    try {
      
      const updatedAddress = {
        ...formData,
        id: editingAddress.id, // Preserve the original ID
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(), // Phone already has +91 prefix
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        pinCode: formData.pinCode.trim(),
        landmark: formData.landmark.trim()
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Get current user data to ensure we have the latest state
      const userDoc = await getDoc(userRef);
      let existingAddresses = [];
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        existingAddresses = userData.saved_addresses || [];
      }
      
      // If setting as default, remove default from other addresses
      let updatedAddresses = existingAddresses.map(addr => {
        if (addr.id === editingAddress.id) {
          return updatedAddress;
        } else {
          // Remove default from other addresses if this one is being set as default
          if (updatedAddress.default) {
            return { ...addr, default: false };
          }
          return addr;
        }
      });
      
      
      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userRef, {
        saved_addresses: updatedAddresses,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setAddresses(updatedAddresses);
      
      // Show premium success message on main page
      let successMessage = `Address for ${updatedAddress.firstName} ${updatedAddress.lastName} has been updated`;
      if (updatedAddress.default) {
        successMessage += ' and set as your preferred delivery location';
      }
      showMainPageSuccess(successMessage);
      
      resetForm();
    } catch (error) {
      console.error('Error updating address:', error);
      showFormError('Failed to update address. Please try again.');
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Get current user data to ensure we have the latest state
      const userDoc = await getDoc(userRef);
      let existingAddresses = [];
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        existingAddresses = userData.saved_addresses || [];
      }
      
      // Find the address to delete for success message
      const addressToDelete = existingAddresses.find(addr => addr.id === addressId);
      
      const updatedAddresses = existingAddresses.filter(addr => addr.id !== addressId);
      
      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userRef, {
        saved_addresses: updatedAddresses,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setAddresses(updatedAddresses);
      setDeleteConfirm(null);
      
      // Show premium success message on main page
      if (addressToDelete) {
        showMainPageSuccess(`Address for ${addressToDelete.firstName} ${addressToDelete.lastName} has been removed from your collection`);
      }

    } catch (error) {
      console.error('Error deleting address:', error);
      showFormError('Failed to delete address. Please try again.');
    }
  };

  const setDefaultAddress = async (addressId) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Get current user data to ensure we have the latest state
      const userDoc = await getDoc(userRef);
      let existingAddresses = [];
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        existingAddresses = userData.saved_addresses || [];
      }
      
      // First, set all addresses to non-default
      // Then, set only the selected address as default
      const updatedAddresses = existingAddresses.map(addr => ({
        ...addr,
        default: addr.id === addressId
      }));

      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userRef, {
        saved_addresses: updatedAddresses,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setAddresses(updatedAddresses);
      
      // Show premium success message on main page
      const addressName = updatedAddresses.find(addr => addr.id === addressId);
      if (addressName) {
        showMainPageSuccess(`"${addressName.firstName} ${addressName.lastName}" is now your preferred delivery location`);
      }
    } catch (error) {
      console.error('Error setting default address:', error);
      showFormError('Failed to set default address. Please try again.');
    }
  };

  const editAddress = (address) => {
    setFormData({
      firstName: address.firstName || '',
      lastName: address.lastName || '',
      email: address.email || '',
      phone: address.phone || '', // Keep +91 prefix when editing
      address: address.address || '',
      city: address.city || '',
      state: address.state || '',
      pinCode: address.pinCode || '',
      landmark: address.landmark || '',
      default: address.default || false
    });
    setEditingAddress(address);
    setShowAddForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingAddress) {
      updateAddress();
    } else {
      addAddress();
    }
  };

  if (loading) {
    return (
      <div className="addresses-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="addresses-container">
      {/* Premium Success Messages */}
      {successMessage && (
        <div className="main-page-message success" style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          background: 'linear-gradient(135deg, #faf8f3 0%, #f5f2ea 100%)',
          color: '#4a1528',
          padding: '1.25rem 1.75rem',
          borderRadius: '12px',
          border: '2px solid #d3b47a',
          boxShadow: '0 8px 32px rgba(74, 21, 40, 0.15), 0 4px 16px rgba(211, 180, 122, 0.2)',
          zIndex: 1000,
          maxWidth: '450px',
          animation: 'slideInRight 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fontFamily: 'Playfair Display, serif',
          fontSize: '0.95rem',
          fontWeight: '500',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
            <span style={{
              fontSize: '1.1rem',
              color: '#d3b47a',
              filter: 'drop-shadow(0 2px 4px rgba(211, 180, 122, 0.3))'
            }}>✨</span>
            <span style={{lineHeight: '1.4'}}>{successMessage}</span>
          </div>
        </div>
      )}
      
      {errorMessage && (
        <div className="main-page-message error" style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          background: 'linear-gradient(135deg, #faf8f3 0%, #f5f2ea 100%)',
          color: '#8b0000',
          padding: '1.25rem 1.75rem',
          borderRadius: '12px',
          border: '2px solid #8b0000',
          boxShadow: '0 8px 32px rgba(139, 0, 0, 0.15), 0 4px 16px rgba(139, 0, 0, 0.1)',
          zIndex: 1000,
          maxWidth: '450px',
          animation: 'slideInRight 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fontFamily: 'Playfair Display, serif',
          fontSize: '0.95rem',
          fontWeight: '500',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
            <span style={{
              fontSize: '1.1rem',
              color: '#8b0000',
              filter: 'drop-shadow(0 2px 4px rgba(139, 0, 0, 0.2))'
            }}>⚠️</span>
            <span style={{lineHeight: '1.4'}}>{errorMessage}</span>
          </div>
        </div>
      )}
      
      {/* Show header and content only when NOT editing */}
      {!showAddForm && (
        <>
          <div className="addresses-header">
            <h1>Saved Addresses</h1>
            <p className="subheading">Manage your delivery addresses for quick checkout</p>
          </div>

                      {/* Addresses List */}
            <div className="addresses-content">
              {addresses.length > 0 && (
                <button 
                  className="add-address-btn"
                  onClick={() => setShowAddForm(true)}
                >
                  <FaPlus /> Add New Address
                </button>
              )}

              {addresses.length === 0 ? (
              <div className="luxury-empty-state">
                <div className="luxury-empty-icon">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="30" stroke="#d3b47a" strokeWidth="2" fill="none"/>
                    <path d="M32 12C24.268 12 18 18.268 18 26C18 38 32 52 32 52S46 38 46 26C46 18.268 39.732 12 32 12Z" stroke="#d3b47a" strokeWidth="2" fill="none"/>
                    <circle cx="32" cy="26" r="4" fill="#d3b47a"/>
                    <path d="M32 8L34 12L30 12L32 8Z" fill="#d3b47a"/>
                  </svg>
                </div>
                <h3 className="luxury-empty-title">No saved addresses</h3>
                <p className="luxury-empty-subtitle">Add your first address to start quick checkout</p>
                <button 
                  className="luxury-cta-button"
                  onClick={() => setShowAddForm(true)}
                >
                  Add New Address
                </button>
              </div>
            ) : (
              <div className="addresses-grid">
                {addresses.map((address, index) => (
                  <div 
                    key={address.id || `address_${index}`} 
                    className={`address-card ${address.default ? 'default' : ''}`}
                    data-city={address.city}
                    data-state={address.state}
                    data-address-id={address.id || `address_${index}`}
                  >
                    {address.default && (
                      <div className="default-badge">
                        <FaStarSolid /> Default
                      </div>
                    )}
                    
                    <div className="address-info">
                      <div className="address-header">
                        <h3>{address.firstName} {address.lastName}</h3>
                        {!address.default && (
                          <button
                            className="set-default-btn"
                            onClick={() => setDefaultAddress(address.id || `address_${index}`)}
                            title="Set as default"
                          >
                            <FaStar />
                          </button>
                        )}
                      </div>
                      
                      <p className="address-email">{address.email}</p>
                      <p className="address-phone">{address.phone}</p>
                      <p className="address-text">{address.address}</p>
                      <p className="address-location">
                        {address.city}, {address.state} - {address.pinCode}
                      </p>
                      {address.landmark && (
                        <p className="address-landmark">Near: {address.landmark}</p>
                      )}
                    </div>

                    <div className="address-actions">
                      <button
                        className="edit-btn"
                        onClick={() => editAddress(address)}
                        title="Edit address"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => {
                          const addressId = address.id || `address_${index}`;
                          setDeleteConfirm(addressId);
                        }}
                        title="Delete address"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Address Form - Full Screen Modal */}
      {showAddForm && (
        <div className="address-form-fullscreen">
          <div className="form-top-bar">
            <button className="back-btn" onClick={goBackToMainView}>
              {isMobile ? '' : 'Back'}
            </button>
            <h2 className="form-title">{editingAddress ? 'Edit Address' : 'Add New Address'}</h2>

          </div>
          
          <form onSubmit={handleSubmit} className="address-form-compact">
            {/* Name Row */}
            <div key="name-row" className="form-row">
              <div className="form-group">
                <label htmlFor="firstName" className="required">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName" className="required">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            {/* Contact Row */}
            <div key="contact-row" className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="required">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="required">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 Enter your phone number"
                  required
                />
              </div>
            </div>

            {/* Full Address */}
            <div key="address-field" className="form-group full-width">
              <label htmlFor="address" className="required">Full Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Your complete street address"
                rows="2"
                required
              />
            </div>

            {/* Location Row */}
            <div key="location-row" className="form-row">
              <div className="form-group">
                <label htmlFor="city" className="required">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="state" className="required">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state name"
                  required
                />
              </div>
            </div>

            {/* PIN & Landmark Row */}
            <div key="pin-landmark-row" className="form-row">
              <div className="form-group">
                <label htmlFor="pinCode" className="required">PIN Code</label>
                <input
                  type="text"
                  id="pinCode"
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleInputChange}
                  placeholder="6-digit PIN code"
                  maxLength="6"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="landmark">Landmark (Optional)</label>
                <input
                  type="text"
                  id="landmark"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Nearby landmark (optional)"
                />
              </div>
            </div>

            {/* Default Address Checkbox */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label-simple">
                <input
                  type="checkbox"
                  name="default"
                  checked={formData.default}
                  onChange={handleInputChange}
                />
                <span>Set as Default Address</span>
                <p className="checkbox-help">Fast checkout uses this address by default</p>
              </label>
            </div>

            <div className="form-actions-compact">
              <button type="button" className="btn-cancel" onClick={goBackToMainView}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                {editingAddress ? 'Update Address' : 'Add Address'}
              </button>
            </div>
          </form>
        </div>
      )}




      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" style={{zIndex: 9999}}>
          <div className="modal-content">
            <h3>Delete Address</h3>
            <p>Are you sure you want to delete this address? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => {
                  setDeleteConfirm(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-submit"
                onClick={() => {
                  deleteAddress(deleteConfirm);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Addresses;
